import React, { Suspense, useContext, useEffect, useMemo, useState } from 'react';
import { RouteComponentProps, navigate, useLocation } from '@reach/router';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import MenuIcon from '@mui/icons-material/Menu';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useIsFetching } from '@tanstack/react-query';

import { LoadingCircle } from '../../atoms';
import { SideNav, NotificationCenter, HeaderProfileMenu } from '../../organisms';

import StyledSuperAdmin from './super-admin.style';
import { ApplicationContext, RecentJobsProvider } from '@particles';
import { canOpenPage, firstOpenPage, routes } from '@particles';
import { useMyAccess } from '@api';
import { Errors404 } from '@pages';

const PAGE_TITLES: { path: string; label: string; exact?: boolean }[] = [
  { path: routes.SA_MyProfile, label: 'My Profile' },
  { path: routes.SA_ApiKeys, label: 'API Keys' },
  { path: routes.SA_MarkupMargins, label: 'Markup Margins' },
  { path: routes.SA_AiModels, label: 'AI Models' },
  { path: routes.SA_Lookups, label: 'Lookups' },
  { path: routes.SA_Settings, label: 'Integrations' },
  { path: routes.SA_Core, label: 'Core' },
  { path: routes.SA_NegotiatorDetails, label: 'Negotiator' },
  { path: routes.SA_Unified_Users, label: 'Unified Users' },
  { path: routes.SA_BillingTokens, label: 'Billing Tokens' },
  { path: routes.SA_BillingAccounts, label: 'Billing Accounts' },
  { path: routes.SA_Analytics, label: 'Analytics' },
  { path: routes.SA_Bills, label: 'Bills' },
  { path: routes.SA_Artifacts, label: 'Artifacts' },
  { path: routes.SA_Jobs, label: 'Activity' },
  { path: routes.SA_CustomValues, label: 'Custom Values' },
  { path: routes.SA_ZazuAuditTrails, label: 'ZAZU Audit Trails' },
  { path: routes.SA_Diagnostics, label: 'Extension Reports' },
  { path: routes.SA_SupportTickets, label: 'Support Tickets' },
  { path: routes.SA_DiagnosticsLookup, label: 'Look Up a Code' },
  { path: routes.SA_OAuthGhl, label: 'OAuth' },
  { path: routes.SA_ApiDocs, label: 'API Docs' },
  { path: routes.SA_Roles, label: 'Roles & Staff' },
  { path: routes.SA_Negotiator, label: 'Negotiator', exact: true }
];

function resolvePageTitle(pathname: string): string {
  for (const entry of PAGE_TITLES) {
    if (entry.exact ? pathname === entry.path : pathname.startsWith(entry.path)) {
      return entry.label;
    }
  }
  return 'Dashboard';
}

export interface ErrorProps {
  /** Suspense error */
  error: Error;
}

export interface PageProps extends RouteComponentProps {
  /** Page content */
  children: React.ReactNode;
}

export const SuperAdminTemplate: React.FC<PageProps> = ({ children }: PageProps) => {
  const {
    state: { user }
  } = useContext(ApplicationContext);

  const isSuperAdmin = user?.role.name === 'Super Admin';
  const { pathname } = useLocation();
  // Staff on a custom role (sub-super-admins) get the pages their sections
  // allow; a Super Admin gets everything without waiting on this.
  const { data: access, isLoading: accessLoading } = useMyAccess(user?.id, !isSuperAdmin);
  const isStaff = Boolean(access?.isPlatformUser);
  const canOpen = isSuperAdmin || (isStaff && canOpenPage(pathname, access));
  useEffect(() => {
    if (!isSuperAdmin && isStaff && pathname === routes.SA_Negotiator && !canOpen) {
      navigate(firstOpenPage(access), { replace: true });
    }
  }, [isSuperAdmin, isStaff, pathname, canOpen, access]);
  const pageTitle = useMemo(() => resolvePageTitle(pathname), [pathname]);
  const isFetching = useIsFetching();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Close the mobile drawer whenever the user navigates — SideNav already does
  // this on click, but this catches programmatic navigation too.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname]);

  return (
    <RecentJobsProvider>
      <StyledSuperAdmin className="App">
        <Paper className="sidebar" square elevation={0}>
          <SideNav
            isSuperAdmin={true}
            mobileOpen={mobileNavOpen}
            onMobileClose={() => setMobileNavOpen(false)}
          />
        </Paper>
        <div className="container">
          {isFetching > 0 && (
            <LinearProgress
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 1301,
                height: 3
              }}
            />
          )}
          <Box className="header">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              px={2.5}
              py={1}
              minHeight={48}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <IconButton
                  aria-label="Open navigation"
                  onClick={() => setMobileNavOpen(true)}
                  size="small"
                  sx={{ display: { xs: 'inline-flex', md: 'none' } }}
                >
                  <MenuIcon />
                </IconButton>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1f1f1f' }}>
                  {pageTitle}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={0.5}>
                <NotificationCenter />
                <HeaderProfileMenu />
              </Box>
            </Box>
          </Box>
          <Paper className="main" square elevation={0}>
            <Box component="main" height="100%" display="flex" flexDirection="column">
              <Suspense fallback={<LoadingCircle size={150} />}>
                {canOpen ? (
                  React.isValidElement(children) && React.cloneElement(children)
                ) : !isSuperAdmin && accessLoading ? (
                  <LoadingCircle size={150} />
                ) : (
                  <Errors404 variant="accessDenied" />
                )}
              </Suspense>
            </Box>
          </Paper>
        </div>
      </StyledSuperAdmin>
    </RecentJobsProvider>
  );
};

export default SuperAdminTemplate;
